import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type ExitedTile = { cycle: string; count: number };

export type GradeRow = {
  grade: string;
  mlOnFlashlight: number;
  mlNotOnFlashlight: number;
  flashlightActiveMl: number;
  flashlightNotActiveMl: number;
};

export type DistrictStats = {
  districtName: string;
  sisConnected: boolean;
  flashlightConnected: boolean;
  activeMl?: number;
  monitoredMl?: number;
  exited?: ExitedTile;
  newcomerMl?: number;
  longTermMl?: number;
  mlWithIep?: number;
  benchmarksYtd?: number;
  assignmentsYtd?: number;
  flashlightStudentsNotActiveMl?: number;
  grades: GradeRow[];
};

export const CHART_GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const SIS_COUNTS = ['activeMl', 'monitoredMl', 'newcomerMl', 'longTermMl', 'mlWithIep'] as const;
const FLASHLIGHT_COUNTS = ['benchmarksYtd', 'assignmentsYtd', 'flashlightStudentsNotActiveMl'] as const;
const GRADE_COUNTS = ['mlOnFlashlight', 'mlNotOnFlashlight', 'flashlightActiveMl', 'flashlightNotActiveMl'] as const;

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isGradeRow(value: unknown, grade: string): value is GradeRow {
  return isRecord(value) && value.grade === grade && GRADE_COUNTS.every((key) => isCount(value[key]));
}

function isExited(value: unknown): value is ExitedTile {
  return isRecord(value) && typeof value.cycle === 'string' && isCount(value.count);
}

function isDistrictStats(body: unknown): body is DistrictStats {
  if (!isRecord(body)) return false;
  if (typeof body.districtName !== 'string') return false;
  if (typeof body.sisConnected !== 'boolean' || typeof body.flashlightConnected !== 'boolean') return false;
  const grades = body.grades;
  if (!Array.isArray(grades) || grades.length !== CHART_GRADES.length) return false;
  if (!CHART_GRADES.every((grade, i) => isGradeRow(grades[i], grade))) return false;
  if (body.sisConnected && !(SIS_COUNTS.every((key) => isCount(body[key])) && isExited(body.exited))) return false;
  if (body.flashlightConnected && !FLASHLIGHT_COUNTS.every((key) => isCount(body[key]))) return false;
  return true;
}

export async function fetchDistrictStats(): Promise<DistrictStats> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/district-stats`, { headers: authHeaders(token) });
  if (!response.ok) {
    throw new Error(`district-stats fetch failed: ${response.status}`);
  }
  const body: unknown = await response.json();
  if (!isDistrictStats(body)) {
    throw new Error('district-stats response is missing a count');
  }
  return body;
}
